import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { UserPlus, Mail, Trash2, Shield, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";

const ROLE_BADGE = {
  owner: "bg-neutral-900 text-white",
  editor: "bg-emerald-50 text-emerald-700 border-emerald-200",
  viewer: "bg-neutral-100 text-neutral-700 border-neutral-300",
};

export default function Team() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ email: "", name: "", role: "editor" });
  const [inviting, setInviting] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);

  const load = () => api.get("/team").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const invite = async (e) => {
    e.preventDefault();
    setInviting(true);
    try {
      await api.post("/team/invite", form);
      toast.success("Collaboratore invitato");
      setForm({ email: "", name: "", role: "editor" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setInviting(false); }
  };

  const remove = async (m) => {
    if (!window.confirm(`Rimuovere ${m.email} dal workspace?`)) return;
    await api.delete(`/team/${m.id}`);
    toast.success("Rimosso");
    load();
  };

  const sendTest = async () => {
    setSendingTest(true);
    try {
      const r = await api.post("/team/send-test-digest");
      toast.success(`Email inviata a ${r.data.sent_to} (${r.data.count} gare)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore invio");
    } finally { setSendingTest(false); }
  };

  if (!data) return <div className="p-10">Caricamento...</div>;

  return (
    <div className="p-8 lg:p-10 max-w-4xl space-y-6 fade-up">
      <header>
        <div className="mono-label">Collaboratori</div>
        <h1 className="font-display text-4xl font-medium mt-1">Team & Digest</h1>
        <p className="text-sm text-neutral-500 mt-2">Invita colleghi al tuo workspace e gestisci il digest giornaliero automatico delle scadenze.</p>
      </header>

      {/* Members list */}
      <div className="border border-neutral-200 bg-white">
        <div className="p-5 border-b border-neutral-200 flex items-start justify-between">
          <div>
            <div className="mono-label">Workspace</div>
            <h2 className="font-display text-xl font-medium mt-1">Membri</h2>
          </div>
          <div className={`px-2.5 py-1 border text-xs font-mono uppercase ${data.is_owner ? "brand-bg text-white border-transparent" : "border-neutral-300 text-neutral-700"}`}>
            {data.is_owner ? "Titolare" : "Membro"}
          </div>
        </div>
        <div className="divide-y divide-neutral-200">
          {/* Owner */}
          <div className="p-4 flex items-center gap-3">
            <div className="h-9 w-9 brand-bg text-white flex items-center justify-center font-semibold text-sm">
              {data.owner?.name?.[0]?.toUpperCase() || "S"}
            </div>
            <div className="flex-1">
              <div className="text-sm font-medium">{data.owner?.name || "—"}</div>
              <div className="text-xs text-neutral-500 flex items-center gap-1"><Mail className="h-3 w-3" />{data.owner?.email}</div>
            </div>
            <div className={`px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider ${ROLE_BADGE.owner}`}>Titolare</div>
          </div>
          {data.members.map((m) => (
            <div key={m.id} className="p-4 flex items-center gap-3" data-testid={`team-member-${m.id}`}>
              <div className="h-9 w-9 bg-neutral-200 flex items-center justify-center font-semibold text-sm text-neutral-700">
                {(m.name || m.email)[0].toUpperCase()}
              </div>
              <div className="flex-1">
                <div className="text-sm font-medium">{m.name || m.email}</div>
                <div className="text-xs text-neutral-500 flex items-center gap-1"><Mail className="h-3 w-3" />{m.email}</div>
              </div>
              <div className={`px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider border ${ROLE_BADGE[m.role] || ROLE_BADGE.editor}`}>{m.role}</div>
              {data.is_owner && (
                <button onClick={() => remove(m)} className="text-neutral-400 hover:text-red-600" data-testid={`remove-${m.id}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
          {data.members.length === 0 && <div className="p-6 text-center text-sm text-neutral-500">Nessun collaboratore ancora.</div>}
        </div>
      </div>

      {/* Invite form */}
      {data.is_owner && (
        <div className="border border-neutral-200 bg-white p-6">
          <div className="mono-label mb-3">Invita collaboratore</div>
          <form onSubmit={invite} className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <div className="md:col-span-2"><Label>Email *</Label><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="invite-email" /></div>
            <div><Label>Nome</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="invite-name" /></div>
            <div><Label>Ruolo</Label>
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full border border-neutral-300 px-3 py-2 text-sm bg-white h-10">
                <option value="editor">Editor</option>
                <option value="viewer">Viewer</option>
              </select>
            </div>
            <Button type="submit" disabled={inviting} data-testid="invite-submit" className="brand-bg hover:opacity-90 md:col-span-4"><UserPlus className="h-4 w-4 mr-1.5" /> {inviting ? "Invito..." : "Invita"}</Button>
          </form>
          <p className="mono-label mt-3">Il collaboratore riceverà accesso alle gare del tuo workspace al primo login/registrazione.</p>
        </div>
      )}

      {/* Daily digest */}
      <div className="border border-neutral-200 bg-white p-6">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div>
            <div className="mono-label">Reminder automatico</div>
            <h2 className="font-display text-xl font-medium mt-1">Digest giornaliero delle scadenze</h2>
          </div>
          <Button onClick={sendTest} disabled={sendingTest} variant="outline" data-testid="send-test-digest">
            {sendingTest ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1.5" /> Invia digest di prova</>}
          </Button>
        </div>
        <p className="text-sm text-neutral-600">
          Ogni giorno alle <span className="font-mono font-semibold">08:00</span> (Europe/Rome) riceverai per email l'elenco delle gare con scadenza sotto i 5 giorni. Nessun setup: attivo di default per {user?.email}.
        </p>
        <div className="mt-3 flex items-center gap-2 text-xs text-neutral-500">
          <Shield className="h-3.5 w-3.5" /> Le email non chiedono mai password o codici. Link diretti alle schede gara.
        </div>
      </div>
    </div>
  );
}
